import { FC, ReactNode } from 'react'
import { Skeleton } from 'src/components/Skeleton'
import styles from '../companyProfile.module.scss'

// Section title / subtitle heights come from their font sizes (18 / 13px,
// line-height normal); the field label is 14px; inputs are the spec's 48px.
const TITLE_HEIGHT = 20
const SUBTITLE_HEIGHT = 15
const LABEL_HEIGHT = 16
const INPUT_HEIGHT = 48
const TEXTAREA_HEIGHT = 116
const PRIORITY_ROWS = 5

interface SkeletonSectionProps {
  children: ReactNode
  className?: string
  subtitleWidth: number
  titleWidth: number
}

// Same shell as SectionCard — the real .section class — so the placeholder
// has the section's exact width, padding and background, and only the text
// and inputs are swapped for blocks.
const SkeletonSection: FC<SkeletonSectionProps> = ({
  children,
  className = '',
  subtitleWidth,
  titleWidth,
}) => (
  <section className={`${styles.section} ${className}`}>
    <div className={styles.sectionHead}>
      <div className={styles.sectionHeadText}>
        <Skeleton height={TITLE_HEIGHT} radius={4} width={titleWidth} />
        <Skeleton height={SUBTITLE_HEIGHT} radius={4} width={subtitleWidth} />
      </div>
    </div>
    {children}
  </section>
)

interface SkeletonFieldProps {
  inputHeight?: number
  labelWidth: number
}

const SkeletonField: FC<SkeletonFieldProps> = ({
  inputHeight = INPUT_HEIGHT,
  labelWidth,
}) => (
  <div className={styles.field}>
    <Skeleton height={LABEL_HEIGHT} radius={4} width={labelWidth} />
    <Skeleton height={inputHeight} radius={10} width="100%" />
  </div>
)

/**
 * Stand-in for the Company Profile sections while the profile query is in
 * flight. Mirrors the create form — the spec's canonical layout — card for
 * card, so the page occupies its final height before the data lands instead
 * of growing from the intro alone. Without it, the first visit of a session
 * (nothing cached yet) showed only the "About Your Company" line, and the
 * five sections dropped in a moment later.
 */
export const CompanyProfileSkeleton: FC = () => (
  <div aria-busy className={styles.sections}>
    <SkeletonSection subtitleWidth={250} titleWidth={180}>
      <SkeletonField labelWidth={140} />
    </SkeletonSection>

    <SkeletonSection subtitleWidth={220} titleWidth={190}>
      <SkeletonField labelWidth={90} />
    </SkeletonSection>

    <SkeletonSection
      className={styles.sectionPriorities}
      subtitleWidth={290}
      titleWidth={160}
    >
      <div className={styles.priorityGrid}>
        {Array.from({ length: PRIORITY_ROWS * 2 }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton height={44} key={index} radius={12} width={533} />
        ))}
      </div>
    </SkeletonSection>

    <SkeletonSection subtitleWidth={260} titleWidth={230}>
      <div className={styles.twoColumn}>
        <SkeletonField labelWidth={130} />
        <SkeletonField labelWidth={200} />
      </div>
    </SkeletonSection>

    <SkeletonSection subtitleWidth={420} titleWidth={150}>
      <SkeletonField inputHeight={TEXTAREA_HEIGHT} labelWidth={120} />
    </SkeletonSection>
  </div>
)
